import { useState } from 'react';
import styled from 'styled-components';
import {
  apiErrorText,
  useSetRateCheckTerritoryMutation,
  useSetTenderTerritoryMutation,
} from '@shared/api';
import type { TerritoryCategory } from '@shared/api';
import {
  TERRITORY_HINT,
  TERRITORY_OPTIONS,
  fieldStateFor,
  parseTerritory,
  saveFinished,
  saveStarted,
  territoryLabel,
  territoryStaleNote,
} from './territoryCategory';
import type { TerritoryFieldState } from './territoryCategory';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const CategorySelect = styled.select`
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
  max-width: 100%;
`;

const Note = styled.div<{ $tone: 'warning' | 'danger' | 'muted' }>`
  font-size: 12px;
  color: ${({ theme, $tone }) =>
    $tone === 'muted' ? theme.colors.text.muted : theme.colors.status[$tone]};
`;

interface TerritoryFieldProps {
  /** Карточка, к которой относится поле: ошибка и ожидание — только её. */
  ownerId: string;
  value: TerritoryCategory | null | undefined;
  /** Категория показанного результата; `undefined` — результата нет. */
  resultValue: TerritoryCategory | null | undefined;
  canEdit: boolean;
  /** Идёт анализ или расчёт: он уже взял категорию, смена отключена. */
  busy: boolean;
  busyText: string;
  /** Сохранить категорию; `null` — снять. Ошибку показывает само поле. */
  onSave: (value: TerritoryCategory | null) => Promise<unknown>;
  /** Подпись кнопки повторного анализа на этом экране. */
  rerunLabel: string;
}

/** Категория местности в карточке проверки или расчёта (C05): показ и смена.
 *  Если результат посчитан по другой категории, поле говорит об этом, пока
 *  не пересчитают («Аудит 40», Z1). */
export function TerritoryField({
  ownerId,
  value,
  resultValue,
  canEdit,
  busy,
  busyText,
  onSave,
  rerunLabel,
}: TerritoryFieldProps) {
  const [state, setState] = useState<TerritoryFieldState | null>(null);
  const view = fieldStateFor(state, ownerId);
  const stale = territoryStaleNote(value, resultValue, rerunLabel);

  async function onChange(raw: string) {
    const id = ownerId;
    setState(saveStarted(id));
    try {
      await onSave(parseTerritory(raw));
      setState((s) => saveFinished(s, id, null));
    } catch (err) {
      const text = apiErrorText(err, 'Не удалось сохранить категорию местности.');
      setState((s) => saveFinished(s, id, text));
    }
  }

  if (!canEdit) {
    return (
      <Wrap>
        {territoryLabel(value)}
        {stale && <Note $tone="warning">{stale}</Note>}
      </Wrap>
    );
  }

  return (
    <Wrap>
      <CategorySelect
        value={value ?? ''}
        disabled={view.saving || busy}
        onChange={(e) => void onChange(e.target.value)}
        aria-label="Категория местности"
        title={busy ? busyText : TERRITORY_HINT}
      >
        <option value="">не указана — ставка обычная</option>
        {TERRITORY_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </CategorySelect>
      {busy && <Note $tone="muted">{busyText}</Note>}
      {stale && <Note $tone="warning">{stale}</Note>}
      {view.error && <Note $tone="danger">{view.error}</Note>}
    </Wrap>
  );
}

/** Категория местности в карточке проверки расценок (Ф2). Монтировать с
 *  `key` по id проверки: поле другой проверки не наследует её состояние. */
export function RateCheckTerritory({
  checkId,
  value,
  resultValue,
  status,
  canEdit,
}: {
  checkId: string;
  value: TerritoryCategory | null | undefined;
  /** Категория, по которой посчитан последний анализ. */
  resultValue: TerritoryCategory | null | undefined;
  status: string;
  canEdit: boolean;
}) {
  const [save] = useSetRateCheckTerritoryMutation();
  return (
    <TerritoryField
      ownerId={checkId}
      value={value}
      resultValue={status === 'analyzed' ? (resultValue ?? null) : undefined}
      canEdit={canEdit}
      busy={status === 'analyzing'}
      busyText="Идёт анализ — категорию можно сменить после его окончания."
      onSave={(territory_category) => save({ id: checkId, territory_category }).unwrap()}
      rerunLabel="Запустить ещё раз"
    />
  );
}

/** Категория местности в карточке расчёта по тендеру (Ф5). Монтировать с
 *  `key` по id расчёта. */
export function TenderTerritory({
  estimateId,
  value,
  resultValue,
  status,
  canEdit,
}: {
  estimateId: string;
  value: TerritoryCategory | null | undefined;
  /** Категория, по которой посчитан текущий результат. */
  resultValue: TerritoryCategory | null | undefined;
  status: string;
  canEdit: boolean;
}) {
  const [save] = useSetTenderTerritoryMutation();
  return (
    <TerritoryField
      ownerId={estimateId}
      value={value}
      resultValue={status === 'completed' ? (resultValue ?? null) : undefined}
      canEdit={canEdit}
      busy={status === 'calculating'}
      busyText="Идёт расчёт — категорию можно сменить после его окончания."
      onSave={(territory_category) => save({ id: estimateId, territory_category }).unwrap()}
      rerunLabel="Пересчитать"
    />
  );
}
